import { Injectable } from '@angular/core';
import { ResolveIntentResponse } from './assist.model';

export type AssistEntitlementQueryKind = 'WHO_CAN_QUERY' | 'EXPLAIN_DECISION' | 'EXPLAIN_DENIAL';

export type AssistEntitlementQueryRequest =
  | {
      readonly kind: 'WHO_CAN_QUERY';
      readonly orgId: string;
      readonly actionKind: string;
      readonly accountId?: string | null;
      readonly asOfDate?: string | null;
    }
  | {
      readonly kind: 'EXPLAIN_DECISION';
      readonly orgId: string;
      readonly subjectId: string;
      readonly actionKind: string;
      readonly accountId?: string | null;
      readonly amount?: string | null;
      readonly currency?: string | null;
    };

const ENTITLEMENT_QUERY_KINDS = new Set<string>([
  'WHO_CAN_QUERY',
  'EXPLAIN_DECISION',
  'EXPLAIN_DENIAL',
]);

export function isEntitlementQueryKind(actionKind: string): actionKind is AssistEntitlementQueryKind {
  return ENTITLEMENT_QUERY_KINDS.has(actionKind.toUpperCase());
}

@Injectable({ providedIn: 'root' })
export class AssistEntitlementQueryIntentService {
  buildRequest(
    response: ResolveIntentResponse,
    fallbackOrgId: string | null | undefined,
    fallbackSubjectId: string | null | undefined,
  ): AssistEntitlementQueryRequest | null {
    const kind = response.actionKind.toUpperCase();
    if (!isEntitlementQueryKind(kind)) return null;

    const orgId = this.stringParam(response, 'orgId', 'organizationId') || fallbackOrgId || '';
    const actionKind = this.stringParam(
      response,
      'actionKind',
      'action',
      'entitlementAction',
      'requestedAction',
      'targetActionKind',
    );
    if (!orgId || !actionKind) return null;

    if (kind === 'WHO_CAN_QUERY') {
      return {
        kind: 'WHO_CAN_QUERY',
        orgId,
        actionKind,
        accountId: this.nullableStringParam(response, 'accountId', 'accountRef'),
        asOfDate: this.nullableStringParam(response, 'asOfDate', 'asOf'),
      };
    }

    const subjectId =
      this.stringParam(response, 'subjectId', 'subject', 'actorSubject', 'principalSubject', 'userId') ||
      fallbackSubjectId ||
      '';
    if (!subjectId) return null;

    return {
      kind: 'EXPLAIN_DECISION',
      orgId,
      subjectId,
      actionKind,
      accountId: this.nullableStringParam(response, 'accountId', 'accountRef'),
      amount: this.amountParam(response),
      currency: this.currencyParam(response),
    };
  }

  private nullableStringParam(response: ResolveIntentResponse, ...keys: string[]): string | null {
    return this.stringParam(response, ...keys) || null;
  }

  private stringParam(response: ResolveIntentResponse, ...keys: string[]): string {
    for (const key of keys) {
      const value = response.params[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
      if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    }
    return '';
  }

  private amountParam(response: ResolveIntentResponse): string | null {
    const raw = response.params['amount'];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
    if (raw && typeof raw === 'object' && 'amount' in raw) {
      const amount = (raw as { amount?: unknown }).amount;
      if (typeof amount === 'string' && amount.trim()) return amount.trim();
      if (typeof amount === 'number' && Number.isFinite(amount)) return String(amount);
    }
    return null;
  }

  private currencyParam(response: ResolveIntentResponse): string | null {
    const explicit = this.nullableStringParam(response, 'currency');
    if (explicit) return explicit;
    const raw = response.params['amount'];
    if (raw && typeof raw === 'object' && 'currency' in raw) {
      const currency = (raw as { currency?: unknown }).currency;
      if (typeof currency === 'string' && currency.trim()) return currency.trim();
    }
    return null;
  }
}
